'use client';

import { createContext, use, type ReactNode } from 'react';
import type { Id } from '@/convex/_generated/dataModel';

/**
 * Read scope for the message-rendering tree.
 *
 * The org-chat transcript components (OrgMessageList and everything under it)
 * are reused verbatim by the token-addressed share page
 * (/share/threads/[token]). This context tells deep leaves how to resolve
 * data the member surface gets through auth-gated queries:
 *
 * - 'member' (default): normal behavior, nothing changes.
 * - 'share': the viewer reached the thread through a share token. Member-only
 *   subscriptions must be skipped ('skip' args + hidden affordances), and
 *   media resolution routes through the token-gated share queries for
 *   anonymous viewers (authenticated grantees keep the normal storage
 *   resolver — its thread-share branch authorizes them).
 * - 'fileShare': a file or folder share (/share/files/[token]) rendering the
 *   member file viewer. Member-only subscriptions are skipped the same way;
 *   URLs are resolved by the share page and passed in.
 *
 * Default is 'member' so no member surface needs a provider.
 */
/**
 * What a shared task's Participant needs to answer GA's question in place
 * (#6067): the task and the question it waits on, from `getSharedThread`.
 */
export type ShareAnswerContext = {
  orgThreadId: Id<'orgThreads'>;
  awaitingQuestionToolCallId?: string;
  awaitingFeatureQuestionRequestId?: Id<'featureQuestionRequests'>;
};

export type MessageReadScope =
  | { mode: 'member' }
  | {
      mode: 'share';
      token: string;
      role: 'viewer' | 'participant';
      answer?: ShareAnswerContext;
    }
  | { mode: 'fileShare'; token: string; role: 'viewer' | 'editor' };

const MEMBER_SCOPE: MessageReadScope = { mode: 'member' };

const MessageReadScopeContext = createContext<MessageReadScope>(MEMBER_SCOPE);

export function MessageReadScopeProvider({
  scope,
  children,
}: {
  scope: MessageReadScope;
  children: ReactNode;
}) {
  return (
    <MessageReadScopeContext.Provider value={scope}>
      {children}
    </MessageReadScopeContext.Provider>
  );
}

export function useMessageReadScope(): MessageReadScope {
  return use(MessageReadScopeContext);
}

/** True when rendering inside any share page (chat or file share). */
export function useIsShareReadScope(): boolean {
  return use(MessageReadScopeContext).mode !== 'member';
}

/** The answer context for a shared task's Participant, else null (#6067). */
export function useShareAnswerContext(): ShareAnswerContext | null {
  const scope = use(MessageReadScopeContext);
  return scope.mode === 'share' && scope.role === 'participant'
    ? (scope.answer ?? null)
    : null;
}

/**
 * Question cards are read-only for every share reader except a shared task's
 * Participant, who may answer (#6026 rule, #6067).
 */
export function useIsQuestionReadOnlyScope(): boolean {
  const scope = use(MessageReadScopeContext);
  return (
    scope.mode !== 'member' &&
    !(scope.mode === 'share' && scope.role === 'participant')
  );
}
